import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Form, Modal, Space, Switch, Tag, Select, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getEndpoint } from '../utils/endpoints';

interface Client {
  client_code: string;
  client_name: string;
  description: string;
  active: boolean;
  settings: {
    translation_directions: string[];
    managed_domains: string[];
  };
}

const ClientSetup: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = () => {
    axios
      .get(`${getEndpoint('GLOSS')}/getClients/`)
      .then((response) => setClients(response.data))
      .catch((error) => {
        console.error('Error fetching clients:', error);
        message.error('Errore nel caricamento dei clienti.');
      });
  };

  const handleAddClient = () => {
    setEditingClient(null);
    form.resetFields(); // Clear the form fields
    setIsModalVisible(true);
  };

  const handleEditClient = (record: Client) => {
    setEditingClient(record);
    form.setFieldsValue(record); // Populate the form with the selected client's data
    setIsModalVisible(true);
  };

  const handleDeleteClient = (record: Client) => {
    setDeletingClient(record);
    setDeleteConfirmation(''); // Clear the confirmation input
    setIsDeleteModalVisible(true);
  };

  const confirmDeleteClient = async () => {
    if (deletingClient) {
      try {
        await axios.post(`${getEndpoint('GLOSS')}/deleteClient/`, {
          client_code: deletingClient.client_code,
        });
        setClients((prevClients) =>
          prevClients.filter((client) => client.client_code !== deletingClient.client_code)
        );
        setIsDeleteModalVisible(false);
        message.success('Cliente eliminato.');
      } catch (error) {
        console.error('Error deleting client:', error);
        message.error("Errore nell'eliminazione del cliente.");
      }
    }
  };

  const handleSaveClient = async (values: Client) => {
    try {
      const response = await axios.post(
        `${getEndpoint('GLOSS')}/addClient/${values.client_name}/`,
        values
      );
      const result = response.data;

      if (editingClient) {
        setClients((prevClients) =>
          prevClients.map((client) =>
            client.client_code === editingClient.client_code ? { ...editingClient, ...values } : client
          )
        );
      } else {
        setClients((prevClients) => [...prevClients, result]);
      }

      setIsModalVisible(false);
      fetchClients(); // Refresh the clients list
      message.success(editingClient ? 'Cliente aggiornato.' : 'Cliente creato.');
    } catch (error) {
      console.error('Error saving client:', error);
      message.error('Errore nel salvataggio del cliente.');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'client_name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) => <Switch checked={active} disabled />,
    },
    {
      title: 'Translation Directions',
      dataIndex: ['settings', 'translation_directions'],
      key: 'translation_directions',
      render: (translation_directions: string[]) => (
        <>
          {(translation_directions || []).map((direction) => (
            <Tag color="blue" key={direction}>
              {direction}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: 'Domains',
      dataIndex: ['settings', 'managed_domains'],
      key: 'managed_domains',
      render: (managed_domains: string[]) => (
        <>
          {(managed_domains || []).map((domain) => (
            <Tag color="green" key={domain}>
              {domain}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Client) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEditClient(record)} />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClient(record)}
            danger
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button type="primary" icon={<PlusOutlined />} onClick={handleAddClient} style={{ marginBottom: '16px' }}>
        Add Client
      </Button>
      <Table columns={columns} dataSource={clients} rowKey="client_code" />

      <Modal
        title={editingClient ? 'Edit Client' : 'Add Client'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleSaveClient} layout="vertical">
          <Form.Item
        label="Name"
        name="client_name"
        rules={[{ required: true, message: 'Please enter the client name' }]}
          >
        <Input placeholder="Enter client name" disabled={!!editingClient} />
          </Form.Item>
          <Form.Item
        label="Description"
        name="description"
        rules={[{ required: true, message: 'Please enter a description' }]}
          >
        <Input placeholder="Enter description" />
          </Form.Item>
          <Form.Item label="Active" name="active" valuePropName="checked">
        <Switch />
          </Form.Item>
            <Form.Item
            label="Translation Directions"
            name={['settings', 'translation_directions']}
            rules={[{ required: true, message: 'Please enter translation directions' }]}
            >
            <Select mode="tags" placeholder="Enter translation directions" />
            </Form.Item>
            <Form.Item
            label="Domains"
            name={['settings', 'managed_domains']}
            rules={[{ required: true, message: 'Please enter domains' }]}
            >
            <Select mode="tags" placeholder="Enter domains" />
            </Form.Item>
          <Form.Item>
        <Button type="primary" htmlType="submit">
          Save
        </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Confirm Deletion"
        open={isDeleteModalVisible}
        onCancel={() => setIsDeleteModalVisible(false)}
        onOk={confirmDeleteClient}
        okButtonProps={{ disabled: deleteConfirmation !== 'delete' }}
        okText="Delete"
        cancelText="Cancel"
      >
        <p>Are you sure you want to delete this client? Type "delete" to confirm.</p>
        <Input
          placeholder="Type 'delete' to confirm"
          value={deleteConfirmation}
          onChange={(e) => setDeleteConfirmation(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default ClientSetup;
